import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "~/lib/fetchClient";
import APIRequest from "~/lib/api_request";

const formatLinks = (links) =>
  links.map((l) => ({ ...l, showEdit: false, showText: true }));

const useLeadLinks = (leadId, initialLinks = []) => {
  const [links, setLinks] = useState(() => formatLinks(initialLinks));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const fullContactRequest = useRef(null);

  const serializedLinks = JSON.stringify(initialLinks);
  useEffect(() => {
    setLinks(formatLinks(JSON.parse(serializedLinks)));
  }, [serializedLinks]);

  const createLink = useCallback(
    async (link) => {
      try {
        const result = await apiFetch.post(
          `/leads/${leadId}/links`,
          { json: link },
        );
        setLinks(formatLinks(result));
        setErrors({});
        return true;
      } catch (error) {
        GlobalContainer.notify("Couldn't add link, please try again", "danger");
        setErrors(error?.errors || {});
        return false;
      }
    },
    [leadId],
  );

  const updateLink = useCallback(
    async (link) => {
      try {
        const result = await apiFetch.put(
          `/leads/${leadId}/links/${link.id}`,
          { json: link },
        );
        setLinks(formatLinks(result));
        setErrors({});
        return true;
      } catch (error) {
        GlobalContainer.notify(
          "Couldn't update link, please try again",
          "danger",
        );
        setErrors(error?.errors || {});
        return false;
      }
    },
    [leadId],
  );

  const deleteLink = useCallback(
    async (linkId) => {
      try {
        const result = await apiFetch.delete(
          `/leads/${leadId}/links/${linkId}`,
        );
        setLinks(formatLinks(result));
        setErrors({});
      } catch (error) {
        setErrors(error?.errors || {});
      }
    },
    [leadId],
  );

  const lookupFullContact = useCallback(async () => {
    APIRequest.abort(fullContactRequest.current);
    setLoading(true);

    try {
      fullContactRequest.current = APIRequest.post({
        resource: `/v1/leads/${leadId}/links/autoload`,
      });

      const result = await new Promise((resolve, reject) => {
        fullContactRequest.current.end((error, response) => {
          if (error) {
            reject(error);
            return;
          }
          resolve(response.body);
        });
      });

      setLinks(formatLinks(result));
      setErrors({});
      setLoading(false);
      return true;
    } catch (error) {
      GlobalContainer.notify(
        "Something went wrong during lookup, please try again",
        "danger",
      );
      setErrors(error?.response?.body?.errors || {});
      setLoading(false);
      return false;
    }
  }, [leadId]);

  useEffect(
    () => () => {
      APIRequest.abort(fullContactRequest.current);
    },
    [],
  );

  const showEditLink = useCallback((linkId) => {
    setLinks((prev) =>
      prev.map((l) =>
        l.id === linkId
          ? { ...l, showEdit: true, showText: false }
          : { ...l, showEdit: false, showText: true },
      ),
    );
  }, []);

  const resetLinks = useCallback(() => {
    setLinks((prev) => formatLinks(prev));
  }, []);

  return {
    links,
    loading,
    errors,
    createLink,
    updateLink,
    deleteLink,
    lookupFullContact,
    showEditLink,
    resetLinks,
  };
};

export default useLeadLinks;
